// Vendor directory (Slice 4). Reads ONLY the cached snapshot from IndexedDB —
// zero network / MCP at render time, so it works on the floor offline. Two modes:
//   • Vendors — fuzzy-search our supplier records (bridges a booth's vendor name
//     to "likely match — confirm"); tap → a Vendor 360 panel.
//   • Product Lookup — fuzzy-match a product against our catalog (name, SKU, our
//     cost, retail, margin); the fuzzy score ranks + tolerates typos but is never
//     shown. Vendor matches stay "likely — confirm" (right company); product
//     lookups just list the matches.

import { getSnapshot } from '../db/snapshot';
import { fetchVendor360, isNotAuthed } from '../sync/api';
import { getIdentity } from '../auth/auth';
import { renderSessionExpired } from '../auth/sessionRecovery';
import type { VendorRow, CatalogRow } from '../snapshot/types';
import type { Vendor360 } from './types';
import { rankMatches } from './match';

type Mode = 'vendors' | 'carry';

export async function renderVendors(root: HTMLElement): Promise<void> {
  const snap = await getSnapshot();
  if (!snap) {
    root.innerHTML = `<div class="empty">No snapshot on this device yet.<br/>Build one on the <a href="#/data" class="link">Settings</a> tab (on wifi), then look up vendors here — offline.</div>`;
    return;
  }

  root.innerHTML = `
    <section class="card vendors">
      <div class="segmented" id="mode">
        <button type="button" class="seg seg--on" data-mode="vendors">Vendors</button>
        <button type="button" class="seg" data-mode="carry">Product Lookup</button>
      </div>
      <div class="vendors__fresh">Snapshot ${fmtDate(snap.built_at)} · ${snap.counts.vendors} vendors · ${snap.counts.catalog} products</div>
      <label class="field">
        <span id="v-label">Find a vendor</span>
        <input id="v-search" class="control" placeholder="Vendor name (e.g. a booth's brand)…" autocapitalize="none" autocomplete="off" />
      </label>
      <div id="v-body"></div>
    </section>
  `;

  const modeEl = root.querySelector<HTMLDivElement>('#mode')!;
  const labelEl = root.querySelector<HTMLElement>('#v-label')!;
  const searchEl = root.querySelector<HTMLInputElement>('#v-search')!;
  const bodyEl = root.querySelector<HTMLDivElement>('#v-body')!;
  let mode: Mode = 'vendors';

  modeEl.querySelectorAll<HTMLButtonElement>('.seg').forEach((btn) => {
    btn.addEventListener('click', () => {
      mode = btn.dataset.mode as Mode;
      modeEl.querySelectorAll('.seg').forEach((b) => b.classList.remove('seg--on'));
      btn.classList.add('seg--on');
      labelEl.textContent = mode === 'vendors' ? 'Find a vendor' : 'Product Lookup';
      searchEl.placeholder =
        mode === 'vendors' ? "Vendor name (e.g. a booth's brand)…" : 'Product name or description…';
      searchEl.value = '';
      searchEl.focus();
      runSearch();
    });
  });

  let t: ReturnType<typeof setTimeout> | null = null;
  searchEl.addEventListener('input', () => {
    if (t) clearTimeout(t);
    t = setTimeout(runSearch, 150);
  });

  function runSearch(): void {
    const q = searchEl.value;
    if (mode === 'vendors') renderVendorList(bodyEl, snap!.vendors, q, showVendor360);
    else renderCarryResults(bodyEl, snap!.catalog, q);
  }

  function showVendor360(v: VendorRow): void {
    renderVendor360(bodyEl, v, () => runSearch());
  }

  runSearch();
}

// Standalone Vendor 360 route (#/vendor/{id}). Reached from a product report's
// buy-side link (or a deep link). Renders the same panel as the in-directory
// drill-down: vendor basics from the cached snapshot when we have the row, else
// a minimal panel keyed by id that the live get_vendor_360 drill-down fills in.
export async function renderVendorById(
  root: HTMLElement,
  vendorId: string,
  back: () => void,
): Promise<void> {
  const snap = await getSnapshot();
  const row: VendorRow =
    snap?.vendors.find((v) => v.vendor_id === vendorId) ?? {
      vendor_id: vendorId,
      vendor_name: '',
      contact: null,
      phone: null,
      po_count: null,
      merch_spend: null,
      total_spend: null,
      last_order_date: null,
    };
  root.innerHTML = `<section class="card vendors"><div id="v-body"></div></section>`;
  renderVendor360(root.querySelector<HTMLElement>('#v-body')!, row, back);
}

// ── Vendor list / browse ────────────────────────────────────────────────────
function renderVendorList(
  body: HTMLElement,
  vendors: VendorRow[],
  query: string,
  onPick: (v: VendorRow) => void,
): void {
  const q = query.trim();
  const ranked = q
    ? rankMatches(q, vendors, (v) => v.vendor_name || v.vendor_id, { limit: 25 })
    : [...vendors]
        .sort((a, b) => num(b.total_spend) - num(a.total_spend))
        .slice(0, 25)
        .map((v) => ({ item: v, score: 1 }));

  if (!ranked.length) {
    body.innerHTML = `<div class="miss">No vendor matches “${esc(q)}” in the snapshot — they may be a new supplier.</div>`;
    return;
  }

  const header = q ? '' : `<div class="vendors__browsehint">Top suppliers by spend — or search above.</div>`;
  body.innerHTML =
    header +
    `<ul class="vendor-list">${ranked
      .map(({ item: v, score }, i) => {
        const fuzzy = q && score < 0.999 ? `<span class="chip chip--match">likely · ${Math.round(score * 100)}%</span>` : '';
        return `
        <li class="vendor-row" data-i="${i}" role="button" tabindex="0">
          <div class="vendor-row__name">${esc(v.vendor_name || v.vendor_id)} ${fuzzy}</div>
          <div class="vendor-row__meta">
            ${supplierBadge(v)}
            <span>${fmtMoney(v.total_spend)} spend</span>
            <span>${v.po_count ?? 0} POs</span>
          </div>
        </li>`;
      })
      .join('')}</ul>`;

  body.querySelectorAll<HTMLLIElement>('.vendor-row').forEach((li) => {
    const pick = () => onPick(ranked[Number(li.dataset.i)].item);
    li.addEventListener('click', pick);
    li.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        pick();
      }
    });
  });
}

// ── Vendor 360 ──────────────────────────────────────────────────────────────
function renderVendor360(body: HTMLElement, v: VendorRow, back: () => void): void {
  const ordered = num(v.total_spend) > 0 || (v.po_count ?? 0) > 0;
  body.innerHTML = `
    <div class="v360">
      <button type="button" class="link" id="v-back">‹ Back to results</button>
      <h3 class="v360__name">${esc(v.vendor_name || v.vendor_id)}</h3>
      <div class="v360__flag ${ordered ? 'v360__flag--yes' : 'v360__flag--no'}">
        ${ordered ? '✓ Existing supplier — we have ordered from them' : 'Known vendor — no purchase history on file'}
      </div>
      <dl class="v360__grid">
        ${fact('Total spend', fmtMoney(v.total_spend))}
        ${fact('Merch spend', fmtMoney(v.merch_spend))}
        ${fact('Purchase orders', v.po_count != null ? String(v.po_count) : '—')}
        ${fact('Last order', v.last_order_date ? fmtDate(String(v.last_order_date)) : '—')}
        ${fact('Contact', v.contact ? esc(v.contact) : '—')}
        ${fact('Phone', v.phone ? esc(v.phone) : '—')}
      </dl>
      <h4 class="v360__phead">Products we buy from them</h4>
      <div id="v-products">${productsLoadingHtml()}</div>
    </div>
  `;
  body.querySelector('#v-back')!.addEventListener('click', back);
  void loadVendorProducts(body.querySelector<HTMLElement>('#v-products')!, v);
}

// ── Vendor buy-side products (ONLINE drill-down via get_vendor_360) ──────────
// The vendor basics above render offline from the snapshot; this section is the
// one live call. It never blocks the panel — it fills in (or degrades) on its own.
async function loadVendorProducts(el: HTMLElement, v: VendorRow): Promise<void> {
  if (!navigator.onLine) {
    el.innerHTML = `<div class="v360__signal">📡 Offline — the product list needs signal. Vendor basics above are cached.</div>`;
    return;
  }

  // v1.2: this load is kicked off by the panel rendering, NOT by a tap, so we must
  // not try to open a sign-in popup from here — iOS Safari would block it. When the
  // session is already known dead, skip the doomed request and offer the button
  // instead; the retry then runs inside a real gesture.
  if (getIdentity().expired) {
    renderSessionExpired(
      el,
      { what: 'the product list', className: 'session-x--inline' },
      () => loadVendorProducts(el, v),
    );
    return;
  }

  el.innerHTML = productsLoadingHtml();
  try {
    const report = await fetchVendor360(v.vendor_id, true);
    el.innerHTML = vendorProductsHtml(report);
    wireProductRows(el);
  } catch (err) {
    // The token died between the probe and this call and the interactive retry
    // couldn't recover it (cancelled, or Safari ate the popup). Same one-tap fix.
    if (isNotAuthed(err)) {
      renderSessionExpired(
        el,
        { what: 'the product list', className: 'session-x--inline' },
        () => loadVendorProducts(el, v),
      );
      return;
    }
    // The backend self-reports (temp); show its message verbatim.
    const detail = err instanceof Error ? err.message : String(err);
    el.innerHTML = `<div class="v360__signal v360__signal--warn">Product list unavailable — ${esc(detail)}</div>`;
  }
}

function productsLoadingHtml(): string {
  return `<div class="v360__ploading">Pulling what we buy from them…</div>`;
}

function vendorProductsHtml(r: Vendor360): string {
  const products = r.products ?? [];
  if (r.error || !products.length) {
    return `<div class="miss">No purchase lines on file for this supplier.</div>`;
  }
  return `<ul class="vendor-list">${products
    .map(
      (p) => {
        // Defensive: the MCP get_vendor_360 fix coalesces name → Sage
        // item_description → prod_itemID, so a null name should not reach us.
        // Belt-and-suspenders in case an old backend or a truly-missing name does.
        const label = p.name && String(p.name).trim() ? p.name : (p.prod_itemID || 'Unknown item');
        return `
      <li class="vendor-row" data-sku="${esc(p.prod_itemID)}" role="button" tabindex="0">
        <div class="vendor-row__name">${esc(label)} <span class="vendor-row__go">›</span></div>
        <div class="vendor-row__meta">
          <span>cost ${fmtMoney(p.avg_unit_cost)}/unit</span>
          <span>${fmtQty(p.qty)} bought</span>
          <span>${fmtMoney(p.spend)} spend</span>
          ${p.last_purchase ? `<span>last ${fmtDate(p.last_purchase)}</span>` : ''}
        </div>
        <div class="vendor-row__sku">${esc(p.prod_itemID)}</div>
      </li>`;
      },
    )
    .join('')}</ul>`;
}

// ── Do we carry this? (catalog match) ───────────────────────────────────────
function renderCarryResults(body: HTMLElement, catalog: CatalogRow[], query: string): void {
  const q = query.trim();
  if (!q) {
    body.innerHTML = `<div class="vendors__browsehint">Type a product name or description to check it against our catalog.</div>`;
    return;
  }
  // The fuzzy score is used only to rank + tolerate typos — never shown.
  const ranked = rankMatches(q, catalog, (c) => `${c.prod_name} ${c.category ?? ''}`, { limit: 20, threshold: 0.35 });
  if (!ranked.length) {
    body.innerHTML = `<div class="miss">No products found for “${esc(q)}”.</div>`;
    return;
  }
  body.innerHTML = `<ul class="vendor-list">${ranked
    .map(({ item: c }) => {
      const cost = num(c.latest_cost);
      const retail = num(c.retail);
      const margin = retail > 0 && cost > 0 ? `${Math.round(((retail - cost) / retail) * 100)}% margin` : '';
      return `
      <li class="vendor-row" data-sku="${esc(c.sku)}" role="button" tabindex="0">
        <div class="vendor-row__name">${esc(c.prod_name || c.sku)} <span class="vendor-row__go">›</span></div>
        <div class="vendor-row__meta">
          <span>${esc(c.category || '—')}</span>
          ${c.variant ? `<span>${esc(c.variant)}</span>` : ''}
          <span>cost ${fmtMoney(c.latest_cost)}</span>
          <span>retail ${fmtRetail(c.retail)}</span>
          ${margin ? `<span>${margin}</span>` : ''}
        </div>
        <div class="vendor-row__sku">${esc(c.family)} · ${esc(c.sku)}</div>
      </li>`;
    })
    .join('')}</ul>`;

  wireProductRows(body);
}

// Wire product rows (data-sku) → the live product detail route. Shared by the
// Product Lookup list and the vendor drill-down; the report screen handles the
// online/offline split, so here we just navigate.
function wireProductRows(container: HTMLElement): void {
  container.querySelectorAll<HTMLLIElement>('.vendor-row[data-sku]').forEach((li) => {
    const go = () => {
      const sku = li.dataset.sku;
      if (sku) location.hash = `#/product/${encodeURIComponent(sku)}`;
    };
    li.addEventListener('click', go);
    li.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        go();
      }
    });
  });
}

// ── helpers ─────────────────────────────────────────────────────────────────
function supplierBadge(v: VendorRow): string {
  const ordered = num(v.total_spend) > 0 || (v.po_count ?? 0) > 0;
  return ordered
    ? '<span class="chip chip--supplier">existing supplier</span>'
    : '<span class="chip chip--show">known vendor</span>';
}

function fact(label: string, value: string): string {
  return `<div class="v360__fact"><dt>${label}</dt><dd>${value}</dd></div>`;
}

function num(v: string | number | null): number {
  if (v == null || v === '') return 0;
  const m = String(v).match(/-?\d+(\.\d+)?/); // handles range strings like "29.99-37.99"
  return m ? Number(m[0]) : 0;
}

function fmtMoney(v: string | null): string {
  const n = num(v);
  if (v == null || v === '' || !Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: n < 100 ? 2 : 0 })}`;
}

function fmtQty(v: string | null): string {
  const n = num(v);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—';
}

function fmtRetail(v: string | null): string {
  // Keep range strings ("29.99-37.99") intact; format plain numbers.
  if (v == null || v === '') return '—';
  return /^\s*\d+(\.\d+)?\s*$/.test(v) ? fmtMoney(v) : esc(v);
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? esc(iso.slice(0, 10)) : d.toLocaleDateString();
}

function esc(s: string | null | undefined): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
